/**
 * MetaAdsCampaigns — página "Campanhas" do módulo Meta Ads.
 *
 * Espelha a experiência da página Campanhas do Google Ads (GoogleAds.jsx):
 * mesma barra de filtros, LevelSelector, PageSelector, breadcrumb e drill-down
 * (Contas → Campanhas → Conjuntos → Anúncios), reaproveitando DataTable e
 * MetricFunnel. Diferenças: origem dos dados (/api/meta-ads/*) e ausência de
 * atribuição CRM — as colunas são as métricas de mídia da Meta.
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import MetaAccountFilter, { useMetaAccountFilter } from '../../components/MetaAccountFilter.jsx'
import PageState from '../../components/PageState.jsx'
import CampaignFilter from '../../components/CampaignFilter.jsx'
import { DataTable } from '../../components/DataTable.jsx'
import { MetricFunnel } from '../../components/Metrics.jsx'
import { MetaAuditModal, MetaManageModal } from '../../components/MetaManageModal.jsx'
import { useAccess } from '../../hooks/useAccess.js'
import { useCollapseSidebarOnOverlap } from '../../hooks/useSidebarOverlap.js'
import { useApiData } from '../../hooks/useApiData.js'
import {
  getMetaAdsAccounts, getMetaAdsMetrics,
} from '../../db/api.js'

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENTES AUXILIARES (espelham os do GoogleAds.jsx)
// ═════════════════════════════════════════════════════════════════════════════

function LevelSelector({ levels, activeLevel, onChange }) {
  return (
    <div className="ga-level-selector">
      <span className="ga-level-label">Nível:</span>
      <div className="ga-level-options">
        {levels.map(level => (
          <button
            key={level.id}
            className={`ga-level-btn ${activeLevel === level.id ? 'active' : ''} ${level.disabled ? 'disabled' : ''}`}
            onClick={() => !level.disabled && onChange(level.id)}
            disabled={level.disabled}
          >
            {level.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function PageSelector({ activePage, onChange }) {
  const pages = [
    { id: 'quantitative', label: 'Quantitativa', icon: 'chart' },
    { id: 'rates', label: 'Taxas', icon: 'percent' },
    { id: 'values', label: 'Valores', icon: 'currency' },
  ]
  return (
    <div className="ga-page-selector">
      {pages.map(page => (
        <button
          key={page.id}
          className={`ga-page-btn ${activePage === page.id ? 'active' : ''}`}
          onClick={() => onChange(page.id)}
        >
          <Icon n={page.icon} s={14} />
          {page.label}
        </button>
      ))}
    </div>
  )
}

function Breadcrumb({ items, onNavigate }) {
  return (
    <div className="ga-breadcrumb">
      {items.map((item, index) => (
        <span key={item.id} className="ga-breadcrumb-item">
          {index > 0 && <span className="ga-breadcrumb-separator">›</span>}
          {item.active ? (
            <span className="ga-breadcrumb-active">{item.label}</span>
          ) : (
            <button className="ga-breadcrumb-link" onClick={() => onNavigate(item)}>
              {item.label}
            </button>
          )}
        </span>
      ))}
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// PERSISTÊNCIA DOS FILTROS POR NÍVEL (idêntico ao Google, chave própria Meta)
// ═════════════════════════════════════════════════════════════════════════════

const LEVEL_FILTER_STORAGE_KEY = 'deebs:meta:levelFilters'

function levelFilterStorageKey(clientId) {
  return `${LEVEL_FILTER_STORAGE_KEY}:${clientId ?? 'all'}`
}

function readLevelFilters(clientId) {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(levelFilterStorageKey(clientId))
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

function writeLevelFilters(clientId, value) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(levelFilterStorageKey(clientId), JSON.stringify(value))
  } catch {
    /* sessionStorage indisponível (modo privado) — degrada para só-memória */
  }
}

// ═════════════════════════════════════════════════════════════════════════════
// COLUNAS POR ABA + NÍVEL
// ═════════════════════════════════════════════════════════════════════════════
// Colunas de identidade dependem do nível; as de métrica dependem da aba.

const IDENTITY_COLUMNS = {
  account: [
    { key: 'name', label: 'Conta', sortable: true },
  ],
  campaign: [
    { key: 'name', label: 'Campanha', sortable: true },
    { key: 'accountName', label: 'Conta', sortable: true },
    { key: 'status', label: 'Status', type: 'badge', sortable: true },
  ],
  adset: [
    { key: 'name', label: 'Conjunto', sortable: true },
    { key: 'campaignName', label: 'Campanha', sortable: true },
    { key: 'accountName', label: 'Conta', sortable: true },
    { key: 'status', label: 'Status', type: 'badge', sortable: true },
  ],
  ad: [
    { key: 'name', label: 'Anúncio', sortable: true },
    { key: 'adsetName', label: 'Conjunto', sortable: true },
    { key: 'campaignName', label: 'Campanha', sortable: true },
    { key: 'status', label: 'Status', type: 'badge', sortable: true },
  ],
}

const METRIC_COLUMNS = {
  quantitative: [
    { key: 'spend', label: 'Investimento', format: 'currency', sortable: true, align: 'right' },
    { key: 'impressions', label: 'Impressões', format: 'number', sortable: true, align: 'right' },
    { key: 'clicks', label: 'Cliques', format: 'number', sortable: true, align: 'right' },
    { key: 'inlineLinkClicks', label: 'Cliques no Link', format: 'number', sortable: true, align: 'right' },
    { key: 'reach', label: 'Alcance', format: 'number', sortable: true, align: 'right' },
    { key: 'conversions', label: 'Conversões', format: 'number', sortable: true, align: 'right' },
    { key: 'thruplays', label: 'ThruPlays', format: 'number', sortable: true, align: 'right' },
  ],
  rates: [
    { key: 'ctr', label: 'CTR', format: 'percent', sortable: true, align: 'right' },
    { key: 'cpc', label: 'CPC', format: 'currency', sortable: true, align: 'right' },
    { key: 'cpm', label: 'CPM', format: 'currency', sortable: true, align: 'right' },
    { key: 'cpp', label: 'CPP', format: 'currency', sortable: true, align: 'right' },
    { key: 'costPerConversion', label: 'Custo/Conversão', format: 'currency', sortable: true, align: 'right' },
  ],
  values: [
    { key: 'spend', label: 'Investimento', format: 'currency', sortable: true, align: 'right' },
    { key: 'conversionValue', label: 'Valor de Conversão', format: 'currency', sortable: true, align: 'right' },
    { key: 'roas', label: 'ROAS', format: 'multiplier', sortable: true, align: 'right' },
    { key: 'costPerConversion', label: 'Custo/Conversão', format: 'currency', sortable: true, align: 'right' },
  ],
}

// Recalcula métricas derivadas a partir de brutos somados (nível Conta e totais).
function derive(spend, impressions, clicks, reach, conversions, convValue) {
  return {
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    cpc: clicks > 0 ? spend / clicks : 0,
    cpm: impressions > 0 ? (spend / impressions) * 1000 : 0,
    cpp: reach > 0 ? (spend / reach) * 1000 : 0,
    costPerConversion: conversions > 0 ? spend / conversions : 0,
    roas: spend > 0 ? convValue / spend : 0,
  }
}

const METRIC_KEYS = [
  'spend', 'impressions', 'clicks', 'inlineLinkClicks', 'reach',
  'conversions', 'conversionValue', 'thruplays',
]

function sumMetrics(rows) {
  const acc = Object.fromEntries(METRIC_KEYS.map(k => [k, 0]))
  for (const r of rows) {
    const m = r.metrics || r
    for (const k of METRIC_KEYS) acc[k] += Number(m[k] || 0)
  }
  return acc
}

// ═════════════════════════════════════════════════════════════════════════════
// SUB-PÁGINA: funil (só Quantitativa) + tabela do nível ativo
// ═════════════════════════════════════════════════════════════════════════════

function MetaMetricsPage({ data, level, page, onDrillDown, onManage }) {
  const totals = useMemo(() => {
    const s = sumMetrics(data)
    return { ...s, ...derive(s.spend, s.impressions, s.clicks, s.reach, s.conversions, s.conversionValue) }
  }, [data])

  const columns = useMemo(() => {
    const base = [...IDENTITY_COLUMNS[level], ...METRIC_COLUMNS[page]]
    if (!onManage || level === 'account') return base
    return [...base, {
      key: 'manage', label: 'Ações', sortable: false,
      render: (_, row) => (
        <span style={{ display: 'inline-flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => onManage(row, 'status')}>
            {row.status === 'active' ? 'Pausar' : 'Ativar'}
          </button>
          {level !== 'ad' && (
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => onManage(row, 'budget')}>
              Orçamento
            </button>
          )}
          {level === 'campaign' && (
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => onManage(row, 'duplicate')}>
              Duplicar
            </button>
          )}
        </span>
      ),
    }]
  }, [level, page, onManage])

  const canDrill = level !== 'ad'
  const levelNoun = level === 'account' ? 'por Conta'
    : level === 'campaign' ? 'por Campanha'
    : level === 'adset' ? 'por Conjunto' : 'por Anúncio'

  return (
    <div className="ga-page">
      {page === 'quantitative' && (
        <MetricFunnel
          rows={[
            [
              { label: 'Impressões', value: totals.impressions, format: 'number' },
              { label: 'Cliques', value: totals.clicks, format: 'number' },
              { label: 'Cliques no Link', value: totals.inlineLinkClicks, format: 'number' },
              { label: 'Alcance', value: totals.reach, format: 'number' },
              { label: 'Conversões', value: totals.conversions, format: 'number' },
              { label: 'ThruPlays', value: totals.thruplays, format: 'number' },
            ],
            [
              { label: 'Investimento', value: totals.spend, format: 'currency' },
              { label: 'Valor de Conversão', value: totals.conversionValue, format: 'currency' },
              { label: 'ROAS', value: totals.roas, format: 'multiplier' },
              { label: 'Custo/Conversão', value: totals.costPerConversion, format: 'currency' },
            ],
          ]}
        />
      )}

      <div className="table-section">
        <div className="table-section-header">
          <div>
            <div className="table-section-title">Dados {levelNoun}</div>
            <div className="table-section-subtitle">
              Clique no cabeçalho da coluna para ordenar
              {canDrill ? ' • Clique na linha para detalhar' : ''}
            </div>
          </div>
        </div>
        <DataTable
          key={`${level}-${page}`}
          columns={columns}
          data={data}
          hoverable
          striped
          scrollable
          onRowClick={canDrill ? onDrillDown : undefined}
          emptyText="Nenhum dado encontrado para os filtros selecionados"
        />
      </div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═════════════════════════════════════════════════════════════════════════════

export default function MetaAdsCampaigns() {
  const { activeClient, period } = useApp()
  const navigate = useNavigate()
  useCollapseSidebarOnOverlap(1100)
  const activeClientId = activeClient?.id ?? null
  const { isOrgAdmin } = useAccess()
  const [manageTarget, setManageTarget] = useState(null)
  const [auditOpen, setAuditOpen] = useState(false)

  const { data: accountsData, loading: accLoading, error: accError, reload: reloadAcc } =
    useApiData(getMetaAdsAccounts, [activeClientId], { initialData: [] })

  const { data: campaignsData, loading: campLoading, error: campError, reload: reloadCamp } = useApiData(
    () => getMetaAdsMetrics('campaign', period.start, period.end),
    [period.start, period.end, activeClientId],
    { initialData: [] }
  )

  const { data: adsetsData, loading: adsetLoading, error: adsetError, reload: reloadAdset } = useApiData(
    () => getMetaAdsMetrics('adset', period.start, period.end),
    [period.start, period.end, activeClientId],
    { initialData: [] }
  )

  const { data: adsData, loading: adLoading, error: adError, reload: reloadAd } = useApiData(
    () => getMetaAdsMetrics('ad', period.start, period.end),
    [period.start, period.end, activeClientId],
    { initialData: [] }
  )

  const isLoading = accLoading || campLoading || adsetLoading || adLoading
  const hasError = accError || campError || adsetError || adError
  const reloadAll = () => { reloadAcc(); reloadCamp(); reloadAdset(); reloadAd() }

  const [activeLevel, setActiveLevel] = useState('account')
  const [activePage, setActivePage] = useState('quantitative')
  // Conta no contexto global (mesma escolha da visão geral; zera ao trocar de cliente).
  const { accounts: filterAccounts, accountIds: selectedAccounts, setAccountIds: setSelectedAccounts } = useMetaAccountFilter()
  const [selectedAccount, setSelectedAccount] = useState(null)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [selectedAdset, setSelectedAdset] = useState(null)
  const [levelFilters, setLevelFilters] = useState(() => readLevelFilters(activeClientId))

  const levelFilterIds = activeLevel === 'account' ? null : (levelFilters[activeLevel] ?? null)

  const setLevelFilterIds = useCallback((updater) => {
    setLevelFilters(prev => {
      const current = prev[activeLevel] ?? null
      const nextVal = typeof updater === 'function' ? updater(current) : updater
      const next = { ...prev, [activeLevel]: nextVal }
      writeLevelFilters(activeClientId, next)
      return next
    })
  }, [activeLevel, activeClientId])

  // Troca de cliente invalida drill-down e seleções (IDs não migram).
  useEffect(() => {
    if (activeClientId === null) return
    setSelectedAccounts([])
    setSelectedAccount(null)
    setSelectedCampaign(null)
    setSelectedAdset(null)
    setActiveLevel('account')
    setLevelFilters(readLevelFilters(activeClientId))
  }, [activeClientId])

  // Enriquecimento: achata metrics e resolve nomes de parent p/ as colunas.
  const { ACCOUNTS, CAMPAIGNS, ADSETS, ADS } = useMemo(() => {
    if (isLoading || hasError) {
      return { ACCOUNTS: [], CAMPAIGNS: [], ADSETS: [], ADS: [] }
    }
    const accName = (accId) =>
      accountsData.find(a => String(a.id) === String(accId))?.name || accId || '—'

    const camps = campaignsData.map(c => ({
      ...c, ...c.metrics,
      accountName: accName(c.accountId),
    }))
    const campName = (id) => camps.find(c => String(c.id) === String(id))?.name || '—'

    const adsets = adsetsData.map(g => ({
      ...g, ...g.metrics,
      accountName: accName(g.accountId),
      campaignName: g.campaignName || campName(g.campaignId),
    }))
    const adsetName = (id) => adsets.find(g => String(g.id) === String(id))?.name || '—'

    const ads = adsData.map(a => ({
      ...a, ...a.metrics,
      accountName: accName(a.accountId),
      campaignName: campName(a.campaignId),
      adsetName: adsetName(a.adsetId),
    }))

    // Nível Conta: agrega campanhas por accountId (sem CRM).
    const accs = accountsData.map(a => {
      const own = camps.filter(c => String(c.accountId) === String(a.id))
      const s = sumMetrics(own)
      return {
        ...a, ...s, ...derive(s.spend, s.impressions, s.clicks, s.reach, s.conversions, s.conversionValue),
        campaignCount: own.length,
      }
    })

    return { ACCOUNTS: accs, CAMPAIGNS: camps, ADSETS: adsets, ADS: ads }
  }, [accountsData, campaignsData, adsetsData, adsData, isLoading, hasError])

  // scopedData = linhas do nível ativo, filtradas por drill-down + AccountFilter.
  const scopedData = useMemo(() => {
    let data = []
    if (activeLevel === 'account') {
      data = ACCOUNTS
    } else if (activeLevel === 'campaign') {
      data = selectedAccount
        ? CAMPAIGNS.filter(c => String(c.accountId) === String(selectedAccount.id))
        : CAMPAIGNS
    } else if (activeLevel === 'adset') {
      data = selectedCampaign
        ? ADSETS.filter(g => String(g.campaignId) === String(selectedCampaign.id))
        : ADSETS
    } else if (activeLevel === 'ad') {
      data = selectedAdset
        ? ADS.filter(a => String(a.adsetId) === String(selectedAdset.id))
        : ADS
    }

    if (selectedAccounts.length > 0) {
      const sel = selectedAccounts.map(String)
      data = data.filter(item => sel.includes(String(item.accountId || item.id)))
    }
    return data
  }, [activeLevel, selectedAccount, selectedCampaign, selectedAdset, selectedAccounts, ACCOUNTS, CAMPAIGNS, ADSETS, ADS])

  const levelFilterItems = useMemo(
    () => scopedData.map(item => ({
      id: String(item.id),
      name: item.name || `#${item.id}`,
      source: item.accountName || '',
      status: item.status === 'paused' ? 'paused' : 'active',
    })),
    [scopedData]
  )

  const lvlSelectAll = useCallback(() => setLevelFilterIds(null), [setLevelFilterIds])
  const lvlClearAll = useCallback(() => setLevelFilterIds([]), [setLevelFilterIds])
  const lvlToggle = useCallback((id) => {
    setLevelFilterIds(prev => {
      const base = prev ?? levelFilterItems.map(c => c.id)
      const next = base.includes(id) ? base.filter(x => x !== id) : [...base, id]
      return next.length === levelFilterItems.length ? null : next
    })
  }, [levelFilterItems, setLevelFilterIds])

  const LEVEL_FILTER_TEXTS = {
    campaign: { allLabel: 'Todas as campanhas', nounPlural: 'campanhas', placeholder: 'Buscar campanha...', empty: 'Nenhuma campanha encontrada', selectAll: 'Selecionar todas', none: 'Nenhuma campanha' },
    adset: { allLabel: 'Todos os conjuntos', nounPlural: 'conjuntos', placeholder: 'Buscar conjunto...', empty: 'Nenhum conjunto encontrado', selectAll: 'Selecionar todos', none: 'Nenhum conjunto' },
    ad: { allLabel: 'Todos os anúncios', nounPlural: 'anúncios', placeholder: 'Buscar anúncio...', empty: 'Nenhum anúncio encontrado', selectAll: 'Selecionar todos', none: 'Nenhum anúncio' },
  }

  const levelFilterController = useMemo(() => {
    const texts = LEVEL_FILTER_TEXTS[activeLevel] || LEVEL_FILTER_TEXTS.campaign
    const total = levelFilterItems.length
    const isAllSelected = levelFilterIds === null || levelFilterIds.length === total
    const effectiveIds = isAllSelected ? levelFilterItems.map(c => c.id) : levelFilterIds
    const count = isAllSelected ? total : levelFilterIds.length

    let label
    if (isAllSelected) label = texts.allLabel
    else if (count === 0) label = texts.none
    else if (count === 1) label = levelFilterItems.find(c => c.id === levelFilterIds[0])?.name || `1 ${texts.nounPlural}`
    else label = `${count} ${texts.nounPlural}`

    return {
      available: levelFilterItems,
      selectedIds: effectiveIds,
      isAllSelected,
      isFiltered: !isAllSelected,
      count,
      total,
      label,
      toggle: lvlToggle,
      selectAll: lvlSelectAll,
      clearAll: lvlClearAll,
    }
  }, [activeLevel, levelFilterItems, levelFilterIds, lvlToggle, lvlSelectAll, lvlClearAll])

  const filteredData = useMemo(() => {
    if (levelFilterIds === null) return scopedData
    const set = new Set(levelFilterIds.map(String))
    return scopedData.filter(item => set.has(String(item.id)))
  }, [scopedData, levelFilterIds])

  const handleDrillDown = useCallback((item) => {
    if (activeLevel === 'account') { setSelectedAccount(item); setActiveLevel('campaign') }
    else if (activeLevel === 'campaign') { setSelectedCampaign(item); setActiveLevel('adset') }
    else if (activeLevel === 'adset') { setSelectedAdset(item); setActiveLevel('ad') }
  }, [activeLevel])

  const handleLevelChange = useCallback((level) => {
    setActiveLevel(level)
    if (level === 'account') {
      setSelectedAccount(null); setSelectedCampaign(null); setSelectedAdset(null)
    }
  }, [])

  const handleBreadcrumbNavigate = useCallback((item) => {
    if (item.id === 'accounts') {
      setActiveLevel('account'); setSelectedAccount(null); setSelectedCampaign(null); setSelectedAdset(null)
    } else if (item.id === 'campaigns') {
      setActiveLevel('campaign'); setSelectedCampaign(null); setSelectedAdset(null)
    } else if (item.id === 'adsets') {
      setActiveLevel('adset'); setSelectedAdset(null)
    }
  }, [])

  const breadcrumbItems = useMemo(() => {
    const items = [{ id: 'accounts', label: 'Contas', active: activeLevel === 'account' }]
    if (selectedAccount) items.push({ id: 'campaigns', label: selectedAccount.name || 'Campanhas', active: activeLevel === 'campaign' })
    if (selectedCampaign) items.push({ id: 'adsets', label: selectedCampaign.name, active: activeLevel === 'adset' })
    if (selectedAdset) items.push({ id: 'ads', label: selectedAdset.name, active: activeLevel === 'ad' })
    return items
  }, [activeLevel, selectedAccount, selectedCampaign, selectedAdset])

  const levels = [
    { id: 'account', label: 'Contas' },
    { id: 'campaign', label: 'Campanhas', disabled: activeLevel !== 'account' && !selectedAccount },
    { id: 'adset', label: 'Conjuntos', disabled: activeLevel !== 'campaign' && activeLevel !== 'account' && !selectedCampaign },
    { id: 'ad', label: 'Anúncios', disabled: activeLevel === 'ad' && !selectedAdset },
  ]

  const renderPage = () => {
    return (
      <PageState
        loading={isLoading}
        error={hasError && String(hasError)}
        loadingText="Carregando dados da Meta..."
        onRetry={reloadAll}
      >
      <MetaMetricsPage
        data={filteredData}
        level={activeLevel}
        page={activePage}
        onDrillDown={handleDrillDown}
        onManage={isOrgAdmin && activeClientId ? (row, mode) => setManageTarget({ row, mode }) : undefined}
      />
      </PageState>
    )
  }

  return (
    <Layout
      title="Meta Ads — Campanhas"
      breadcrumb={[{ label: 'Marketing' }, { label: 'Meta Ads', to: '/meta-ads' }, { label: 'Campanhas' }]}
      showPeriod
      topbarRight={<MetaAccountFilter accounts={filterAccounts} selected={selectedAccounts} onChange={setSelectedAccounts} />}
    >
      <div className="ga-container">
        <div className="ga-filters-bar">
          <div className="ga-filters-left">
            {activeLevel !== 'account' && (
              <CampaignFilter
                controller={levelFilterController}
                texts={LEVEL_FILTER_TEXTS[activeLevel]}
              />
            )}
          </div>
          <PageSelector activePage={activePage} onChange={setActivePage} />
          {isOrgAdmin && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAuditOpen(true)}>
              Histórico de alterações
            </button>
          )}
          {isOrgAdmin && activeClientId && accountsData.length > 0 && (
            <button type="button" className="btn btn-black btn-sm" onClick={() => navigate('/meta-ads/campanhas/nova')}>
              Nova campanha
            </button>
          )}
        </div>

        <LevelSelector levels={levels} activeLevel={activeLevel} onChange={handleLevelChange} />

        {selectedAccount && <Breadcrumb items={breadcrumbItems} onNavigate={handleBreadcrumbNavigate} />}

        <div className="ga-content-area">
          {renderPage()}
        </div>
      </div>
      {manageTarget && (
        <MetaManageModal
          row={manageTarget.row}
          level={activeLevel}
          mode={manageTarget.mode}
          clientId={activeClientId}
          onClose={() => setManageTarget(null)}
        />
      )}
      {auditOpen && <MetaAuditModal onClose={() => setAuditOpen(false)} />}
    </Layout>
  )
}
